import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ListChecks, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const PRIO = { low: "bg-slate-700 text-slate-300", medium: "bg-[#ffc70020] text-[#ffc700]", high: "bg-[#ff3b5c20] text-[#ff3b5c]" };
const STATUS = ["todo", "in_progress", "done"];
const STATUS_LBL = { todo: "To Do", in_progress: "In Progress", done: "Done" };

export default function Tasks() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const canManage = user.role === "admin" || user.role === "manager";
  const load = () => api.get("/tasks").then(({ data }) => setRows(data));
  useEffect(() => { load(); api.get("/users").then(({ data }) => setUsers(data)); }, []);
  const grouped = STATUS.reduce((a, s) => { a[s] = rows.filter((r) => r.status === s); return a; }, {});
  const updateStatus = async (id, status) => { await api.patch(`/tasks/${id}`, { status }); load(); };
  const toggleCheck = async (task, idx) => {
    const cl = [...(task.checklist || [])]; cl[idx] = { ...cl[idx], done: !cl[idx].done };
    await api.patch(`/tasks/${task.id}`, { checklist: cl }); load();
  };
  const del = async (id) => { if (!window.confirm("Delete task?")) return; await api.delete(`/tasks/${id}`); load(); };
  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--accent)]">Operations</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Tasks</h1>
        </div>
        {canManage && <button onClick={() => setOpen(true)} data-testid="new-task" className="px-4 py-2.5 rounded-lg font-bold text-black inline-flex items-center gap-2" style={{ background: "var(--accent)" }}><Plus size={16} /> New task</button>}
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {STATUS.map((s) => (
          <div key={s} className="card-surface p-4">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-3 flex justify-between"><span>{STATUS_LBL[s]}</span><span>{grouped[s].length}</span></div>
            <div className="space-y-2">
              {grouped[s].map((t) => (
                <div key={t.id} data-testid={`task-${t.id}`} className="bg-[#0b0d12] border border-[#2a3447] rounded-lg p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-semibold">{t.title}</div>
                    <span className={`pill ${PRIO[t.priority]}`}>{t.priority}</span>
                  </div>
                  {t.description && <div className="text-xs text-slate-400 mt-1 line-clamp-2">{t.description}</div>}
                  {t.checklist?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {t.checklist.map((c, i) => (
                        <label key={`${t.id}-c-${i}`} className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                          <input type="checkbox" checked={!!c.done} onChange={() => toggleCheck(t, i)} data-testid={`task-check-${t.id}-${i}`} className="accent-[color:var(--accent)]" />
                          <span className={c.done ? "line-through text-slate-500" : ""}>{c.text}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  <div className="mt-2 flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">{t.assignee_name} {t.due_date && `· due ${t.due_date}`}</div>
                    <div className="flex gap-1">
                      <select value={t.status} onChange={(e) => updateStatus(t.id, e.target.value)} data-testid={`task-status-${t.id}`} className="bg-[#121620] border border-[#2a3447] rounded text-[10px] px-1 py-0.5">
                        {STATUS.map((ss) => <option key={ss} value={ss}>{STATUS_LBL[ss]}</option>)}
                      </select>
                      {canManage && <button onClick={() => del(t.id)} className="text-slate-500 hover:text-[#ff3b5c]"><Trash2 size={12} /></button>}
                    </div>
                  </div>
                </div>
              ))}
              {grouped[s].length === 0 && <div className="text-xs text-slate-600 text-center py-6"><ListChecks size={20} className="mx-auto mb-2" /> None</div>}
            </div>
          </div>
        ))}
      </div>
      {open && <NewTask users={users} onClose={() => setOpen(false)} onDone={() => { load(); setOpen(false); }} />}
    </div>
  );
}

function NewTask({ users, onClose, onDone }) {
  const [f, setF] = useState({ title: "", description: "", assignee_id: users[0]?.id || "", due_date: "", priority: "medium", checklistText: "" });
  const submit = async (e) => {
    e.preventDefault();
    const checklist = f.checklistText.split("\n").map((s) => s.trim()).filter(Boolean).map((text) => ({ text, done: false }));
    await api.post("/tasks", { title: f.title, description: f.description, assignee_id: f.assignee_id, due_date: f.due_date || null, priority: f.priority, checklist });
    onDone(); toast.success("Task created");
  };
  const inp = "w-full bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)] text-sm";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <form onSubmit={submit} className="card-surface p-6 w-full max-w-md max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="task-modal">
        <h2 className="text-xl font-bold mb-4">New task</h2>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Title *</div><input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="task-title" className={inp} /></label>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Description</div><textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="task-desc" className={inp + " resize-none"} /></label>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Assignee</div><select value={f.assignee_id} onChange={(e) => setF({ ...f, assignee_id: e.target.value })} data-testid="task-assignee" className={inp}>{users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select></label>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <label><div className="text-xs uppercase text-slate-500 mb-1">Priority</div><select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} data-testid="task-priority" className={inp}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select></label>
          <label><div className="text-xs uppercase text-slate-500 mb-1">Due</div><input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="task-due" className={inp} /></label>
        </div>
        <label className="block mb-3"><div className="text-xs uppercase text-slate-500 mb-1">Checklist (one per line)</div><textarea rows={3} value={f.checklistText} onChange={(e) => setF({ ...f, checklistText: e.target.value })} data-testid="task-checklist" className={inp + " resize-none"} /></label>
        <div className="flex justify-end gap-2 mt-4"><button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-400">Cancel</button><button type="submit" data-testid="task-submit" className="px-4 py-2 rounded-lg text-black font-bold text-sm" style={{ background: "var(--accent)" }}>Create</button></div>
      </form>
    </div>
  );
}
